import BaseEntityNew from 'client/components/core/BaseEntity/BaseEntityNew'
import { PAGE_UI_ID } from 'client/constants/editorIds'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useManagement, useWebsiteSettings } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import { EntityProps } from 'client/types'
import * as CommonMenu from 'common/components/entities/Menu'
import { MobileMenuTypesEnum } from 'common/components/entities/Menu/enums/MobileMenuTypesEnum'
import MenuInterface from 'common/components/entities/Menu/types/MenuInterface'
import React from 'react'
import { createPortal } from 'react-dom'
import { getWebsiteThemeBackground } from 'src/editor/store/website-settings/website-settings-selector'

const Menu = ({
  entity,
  isMoveDownAllowed,
  isMoveUpAllowed,
}: EntityProps<MenuInterface>) => {
  const { isMobile } = useDeviceModeContext()
  const isSideMenuOpened = useManagement(managementSelectors.isSideMenuOpened)
  const isDropDownMenuOpened = useManagement(
    managementSelectors.isDropDownMenuOpened,
  )
  const isEditing = useManagement(state =>
    managementSelectors.isEditing(state, entity.id),
  )

  const isSideMenuEmulationMode = isEditing && isSideMenuOpened
  const isDropDownMenuEmulationMode = isEditing && isDropDownMenuOpened

  const background = useWebsiteSettings(getWebsiteThemeBackground)

  return (
    <BaseEntityNew
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
    >
      <CommonMenu.Horizontal
        attrId={entity.htmlAttrId}
        items={entity.menuItems}
        isVisibleOnMobile={!entity.burger}
        alignSelf={entity.alignSelf}
        mobileAlignSelf={entity.mobileAlignSelf}
        padding={entity.padding}
        mobilePadding={entity.mobilePadding}
        spacing={entity.spacing}
        mobileSpacing={entity.mobileSpacing}
        subMenuSpacing={entity.subMenuSpacing}
        activeSubmenuColor={entity.activeSubmenuColor}
        fontSize={entity.fontSize}
        fontFamily={entity.fontFamily}
        fontWeight={entity.fontWeight}
        fontStyle={entity.fontStyle}
        mobileFontSize={entity.mobileFontSize}
        mobileFontFamily={entity.mobileFontFamily}
        mobileFontWeight={entity.mobileFontWeight}
        mobileFontStyle={entity.mobileFontStyle}
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        textAlign={entity.textAlign}
        mobileTextAlign={entity.mobileTextAlign}
        letterSpacing={entity.letterSpacing}
        mobileLetterSpacing={entity.mobileLetterSpacing}
        color={entity.color}
        mobileColor={entity.mobileColor}
        subMenuColor={entity.subMenuColor}
        subMenuBackgroundColor={
          entity.subMenuBackgroundColor || background?.pageBackgroundColor
        }
        activeItemColor={entity.activeColor}
        mobileActiveItemColor={entity.mobileActiveColor}
        itemUnderlineColor={entity.itemUnderlineColor}
        mobileItemUnderlineColor={entity.mobileItemUnderlineColor}
        forceHide={
          (isMobile && isSideMenuEmulationMode) ||
          (isMobile && entity.burger) ||
          (isMobile && isDropDownMenuEmulationMode)
        }
        disablePointerEvents
        isEditing={isEditing}
      />
      {isMobile && (
        <React.Fragment>
          {entity.burger && (
            <CommonMenu.Burger
              burgerAlignSelf={entity.burgerAlignSelf}
              burgerIconColor={entity.burgerIconColor}
              padding={entity.padding}
              mobilePadding={entity.mobilePadding}
              opened={isSideMenuEmulationMode || isDropDownMenuEmulationMode}
              openedColor={
                (entity.mobileMenuType === MobileMenuTypesEnum.side &&
                  entity.mobileColor) ||
                entity.color
              }
              forceVisible={true}
            />
          )}
          {entity.burger &&
            entity.mobileMenuType === MobileMenuTypesEnum.side &&
            document.getElementById(PAGE_UI_ID) &&
            createPortal(
              <CommonMenu.Side
                attrId={entity.htmlAttrId}
                menuAttrId={`side-${entity.htmlAttrId}`}
                items={entity.menuItems}
                opened={isSideMenuEmulationMode}
                alignSelf={entity.alignSelf}
                mobileAlignSelf={entity.mobileAlignSelf}
                sideMenuBackgroundColor={
                  entity?.sideMenuBackgroundColor ||
                  background?.pageBackgroundColor
                }
                itemUnderlineColor={entity.itemUnderlineColor}
                mobileItemUnderlineColor={entity.mobileItemUnderlineColor}
                fontSize={entity.fontSize}
                mobileFontSize={entity.mobileFontSize}
                fontFamily={entity.fontFamily}
                fontWeight={entity.fontWeight}
                fontStyle={entity.fontStyle}
                spacing={entity.mobileSpacing}
                subMenuSpacing={entity.mobileSubMenuSpacing}
                mobileFontFamily={entity.mobileFontFamily}
                mobileFontWeight={entity.mobileFontWeight}
                mobileFontStyle={entity.mobileFontStyle}
                lineHeight={entity.lineHeight}
                mobileLineHeight={entity.mobileLineHeight}
                color={entity.color}
                mobileColor={entity.mobileColor}
                activeColor={entity.activeColor}
                mobileActiveColor={entity.mobileActiveColor}
                forceVisible={isSideMenuEmulationMode}
                isEditing={isEditing}
              />,
              document.getElementById(PAGE_UI_ID) as HTMLElement,
            )}
          {entity.burger &&
            entity.mobileMenuType === MobileMenuTypesEnum.dropDown && (
              <CommonMenu.DropDown
                attrId={entity.htmlAttrId}
                items={entity.menuItems}
                alignSelf={entity.alignSelf}
                mobileAlignSelf={entity.mobileAlignSelf}
                itemUnderlineColor={entity.itemUnderlineColor}
                mobileItemUnderlineColor={entity.mobileItemUnderlineColor}
                fontSize={entity.fontSize}
                spacing={entity.mobileSpacing}
                subMenuSpacing={entity.mobileSubMenuSpacing}
                mobileFontSize={entity.mobileFontSize}
                fontFamily={entity.fontFamily}
                fontWeight={entity.fontWeight}
                fontStyle={entity.fontStyle}
                mobileFontFamily={entity.mobileFontFamily}
                mobileFontWeight={entity.mobileFontWeight}
                mobileFontStyle={entity.mobileFontStyle}
                lineHeight={entity.lineHeight}
                mobileLineHeight={entity.mobileLineHeight}
                color={entity.color}
                mobileColor={entity.mobileColor}
                activeColor={entity.activeColor}
                mobileActiveColor={entity.mobileActiveColor}
                forceVisible={isDropDownMenuEmulationMode}
                isEditing={isEditing}
              />
            )}
        </React.Fragment>
      )}
    </BaseEntityNew>
  )
}

export default Menu
